import React, { useState, useEffect, useContext } from 'react';
import { Modal } from '@appkit4/react-components/modal';
import { Button } from '@appkit4/react-components/button';
import { Combobox } from '@appkit4/react-components';
import { Switch } from '@appkit4/react-components/switch';
import { UserContext } from '@contexts/UserProvider';
import { getAllUsers, shareUserSession } from '../../config/getUserApis';
import { getPublicShareLink, generatePublicShareLink } from '../../config/getSessionApis';
import './shareModal.scss';

interface User {
    label: string;
    value: string;
    email: string;
    isOwner: boolean;
}

interface ShareModalProps {
    visible: boolean;
    title: string;
    activeSessionId: string;
    onCancel: () => void;
    children?: React.ReactNode;
    modalStyle?: React.CSSProperties;
    footerStyle?: React.CSSProperties;
    bodyStyle?: React.CSSProperties;
}

const ShareModal: React.FC<ShareModalProps> = ({
    visible,
    title,
    activeSessionId,
    onCancel,
    //children,
    modalStyle,
    footerStyle,
    bodyStyle,
}) => {
    const [showShareSuccess, setShowShareSuccess] = useState(false);
    const { userEmail } = useContext(UserContext);

    const [publicLink, setPublicLink] = useState<string | null>(null);
    const [isShareLinkEnabled, setIsShareLinkEnabled] = useState(false);
    const [isGeneratingLink, setIsGeneratingLink] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);

    const handleSendInvite = async () => {
        try {
            const users = Array.isArray(selectedUserStrArr) ? selectedUserStrArr.join(',') : selectedUserStrArr;
            await shareUserSession(activeSessionId, users);

            setShowShareSuccess(true);
        } catch (error) {
            console.error('Error in handleSendInvite:', error);
        }
    };

    const handleSuccessClose = () => {
        setShowShareSuccess(false);
    };

    const [usersArr, setUsersArr] = useState<User[]>([]);

    const [selectedUserStrArr, setSelectedUserStrArr] = useState<string[]>([]);

    const handleComboboxSearch = (value: string) => {
        console.log('search', value);
        // search users with value
    };

    const handleComboboxSelect = (value: any) => {
        console.log('Selected users:', value);
        setSelectedUserStrArr(value);
    };

    const handleToggleShareLink = async (checked: boolean) => {
        const previousState = isShareLinkEnabled;
        setIsShareLinkEnabled(checked);
        setIsGeneratingLink(true);

        try {
            const response = await generatePublicShareLink(activeSessionId, checked);

            if (response.status === 'SUCCESS') {
                if (checked && response.resultObject) {
                    setPublicLink(response.resultObject);
                }
            } else {
                setIsShareLinkEnabled(previousState);
                console.error('Failed to update public link:', response.message);
            }
        } catch (error: any) {
            console.error('Error updating public link status:', error);
            setIsShareLinkEnabled(previousState);
        } finally {
            setIsGeneratingLink(false);
        }
    };

    const handleCopyLink = async () => {
        if (!publicLink) return;

        try {
            await navigator.clipboard.writeText(publicLink);
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
        } catch (error) {
            console.error('Failed to copy link:', error);
        }
    };

    useEffect(() => {
        if (visible) {
            setSelectedUserStrArr([]);
            setPublicLink(null);
            setIsShareLinkEnabled(false);
            setLinkCopied(false);

            getPublicShareLink(activeSessionId)
                .then((response) => {
                    if (response.status === 'SUCCESS' && response.resultObject) {
                        const linkInfo = response.resultObject;
                        setPublicLink(linkInfo.link);
                        setIsShareLinkEnabled(linkInfo.isPublic === true);
                    }
                })
                .catch((error) => {
                    console.log('No existing public link:', error);
                });

            getAllUsers().then((users) => {
                console.log('Raw users from API:', users);
                const userList = users.map((u: any) => ({
                    label: u.fullName,
                    value: u.fullName,
                    email: u.email,
                    isOwner: false,
                }));

                setUsersArr(userList.filter((user: any) => user.email !== userEmail));
            });
        }
    }, [visible, userEmail, activeSessionId]);

    useEffect(() => {
        if (showShareSuccess) {
            const timer = setTimeout(() => {
                setShowShareSuccess(false);
                onCancel();
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [showShareSuccess, onCancel]);

    const NotificationSample = () => {
        return (
            <div className="invite-success-wrapper">
                <span className="Appkit4-icon icon-circle-checkmark-outline"></span>
                <span className="ap-typography-body ap-font-weight-2">Invites sent</span>
                <span
                    className="Appkit4-icon icon-close-outline invite-success-close-icon"
                    onClick={handleSuccessClose}></span>
            </div>
        );
    };

    return (
        <Modal
            visible={visible}
            title={title}
            ariaLabel={title}
            onCancel={onCancel}
            modalStyle={{ ...modalStyle, overflow: 'visible', position: 'relative' }}
            footerStyle={footerStyle}
            bodyStyle={{ ...bodyStyle, overflow: 'visible', position: 'relative' }}
            maskCloseable={true}>
            <div className="share-modal-body">
                {showShareSuccess && (
                    <div className="share-modal-invite-success">
                        <NotificationSample />
                    </div>
                )}
                <div className="share-modal-invite">
                    <Combobox
                        style={{ minWidth: '325px' }}
                        className="modal-combobox"
                        data={usersArr}
                        placeholder="Search for a user"
                        value={selectedUserStrArr}
                        valueKey="email"
                        onSearch={handleComboboxSearch}
                        onSelect={handleComboboxSelect}
                        disabled={false}
                        dropdownRenderMode="portal"
                    />
                    <Button
                        className="modal-button"
                        onClick={handleSendInvite}
                        kind="primary"
                        disabled={!selectedUserStrArr || selectedUserStrArr.length === 0}>
                        Send invite
                    </Button>
                </div>

                {/* Public Link Section */}
                <div className="share-modal-public-link">
                    <div className="public-link-toggle-row">
                        <Switch
                            checked={isShareLinkEnabled}
                            onChange={(checked) => handleToggleShareLink(checked)}
                            disabled={isGeneratingLink}>
                            Share link
                        </Switch>
                        <Button
                            kind="text"
                            icon={linkCopied ? 'icon-circle-checkmark-outline' : 'icon-link-outline'}
                            onClick={handleCopyLink}
                            disabled={!isShareLinkEnabled || !publicLink || isGeneratingLink}
                            className="copy-link-button">
                            {linkCopied ? 'Link copied' : 'Copy link'}
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ShareModal;
